<template>
  <div>
    <div
      class="wt-face-on-camera"
      :class="{
        '-active': REC_isRecording,
        '-paused': REC_isPaused,
      }"
    >
      <i
        class="wt2-close wticon wticon-close wt2-color-normal wt2-text-m wt2-text-lm close-icon wt-cursor-pointer"
        @click="closePrompt"
      />

      <div class="-title wt2-text-lm wt2-text-light">Record Video</div>

      <div class="-video-container">
        <div class="-video-holder" :class="{ '-empty': !REC_stream }">
          <div
            class="-info-text -permission-error"
            v-if="!REC_isDisplayRecording && REC_cameraPermissions === false"
          >
            <div class="-title">
              We need access to your camera and microphone
            </div>
            <div class="-subtitle">
              Make sure to give access permission
              <span v-if="showContactSupport">
                , or
                <a @click="contactSupport(0)" class="wt-cursor-pointer"
                  >Contact Support</a
                ><br />
                <a
                  href="https://support.google.com/chrome/answer/2693767"
                  target="_blank"
                >
                  How to enable camera and mic on your chrome browser
                </a>
              </span>
            </div>
          </div>

          <div
            class="-info-text -permission-error"
            v-if="REC_isDisplayRecording && REC_screenPermissions === false"
          >
            <div class="-title">We need access to your screen</div>
            <div class="-subtitle">
              Make sure to give access
              <span v-if="showContactSupport">
                , or
                <a @click="contactSupport(0)" class="wt-cursor-pointer"
                  >Contact Support</a
                ><br />
                <a
                  href="https://support.google.com/chrome/answer/2693767"
                  target="_blank"
                >
                  How to enable camera and mic on your chrome browser
                </a>
              </span>
            </div>
          </div>

          <div
            class="-info-text -pending-screen-capture"
            v-if="isPendingCapture && REC_screenPermissions === null"
          >
            <div class="-title">
              Select your mic and click ‘Start Recording’
            </div>
          </div>

          <wt-player-video
            :hideControls="!hasRecording"
            :class="{ '-disable-pointer': !hasRecording }"
            v-show="!isPendingCapture && recordSupported"
            ref="wtVideoPlayer"
            :supportFullscreenMode="false"
            :autoPlay="false"
          >
            <video ref="Video" oncontextmenu="return false;"></video>
          </wt-player-video>

          <div class="-recording-icon" v-show="REC_isRecording"></div>
          <transition name="fade-countdown">
            <wtFaceOnCameraCountdown
              v-if="showCountDown"
              :seconds="3"
              :dark-mode="true"
            />
          </transition>
        </div>

        <div class="-debug-menu" v-if="debug">
          <div>AUD_deviceId: {{ AUD_deviceId }}</div>
          <div>AUD_volumeMeter: {{ AUD_volumeMeter }}</div>
          <div>AUD_gainValue: {{ AUD_gainValue }}</div>
          <div />
          <div>REC_userMediaSupported: {{ REC_userMediaSupported }}</div>
          <div>REC_cameraPermissions: {{ REC_cameraPermissions }}</div>
          <div>REC_microphonePermissions: {{ REC_microphonePermissions }}</div>
          <div>REC_screenPermissions: {{ REC_screenPermissions }}</div>
          <div>REC_videoDeviceId: {{ REC_videoDeviceId }}</div>
          <div>REC_frameRate: {{ REC_frameRate }}</div>
          <div>REC_isRecording: {{ REC_isRecording }}</div>
          <div>REC_isPaused: {{ REC_isPaused }}</div>
          <div>REC_timings: {{ REC_timings }}</div>
          <div>REC_isDisplayRecording: {{ REC_isDisplayRecording }}</div>
          <div />
          <div>showCountDown: {{ showCountDown }}</div>
          <div>hasRecording: {{ !!hasRecording }}</div>
          <div>readableDuration: {{ readableDuration }}</div>
        </div>
      </div>

      <div class="-bottom">
        <div class="-left">
          <wt-button
            v-if="hasRecording"
            tabindex="0"
            type="hollow-dark"
            class="-download"
            title="Download"
            :icon="'wticon-download2'"
            :text="''"
            @mouseup.native="download"
          />

          <div v-if="hasRecording">
            <wt-toggle
              v-model="$data.autoTranscribe"
              :label-left="'Transcribe video'"
              @toggle="$_onAutoTranscribeToggle"
            />
          </div>

          <div
            class="-record-volume-meter"
            v-if="REC_isRecording"
            :class="{ '-disabled': AUD_isNoMicrophone }"
          >
            <span
              class="wticon"
              :class="{
                'wticon-mic': !AUD_isNoMicrophone && REC_microphonePermissions,
                'wticon-mic-mute':
                  AUD_isNoMicrophone || !REC_microphonePermissions,
              }"
            />
            <WtVolumeMeter
              v-if="!AUD_isNoMicrophone && REC_microphonePermissions"
              :volume="AUD_volumeMeter"
            />
          </div>

          <wt-dropdown-view
            v-if="!hasRecording && !REC_isRecording && !showCountDown"
            type="top"
            :disabled="
              REC_isRecording ||
              !REC_devices.audio.length ||
              AUD_deviceLoading ||
              REC_videoDeviceLoading
            "
            ref="ddAudio"
          >
            <template slot="label">
              <span
                class="wticon"
                :class="{
                  'wticon-mic':
                    !AUD_isNoMicrophone && REC_microphonePermissions,
                  'wticon-mic-mute':
                    AUD_isNoMicrophone || !REC_microphonePermissions,
                }"
              ></span>
              <span class="-label">{{ currentAudioDevice.label }}</span>
              <i class="-icon -chevron wticon wticon-chevron-down" />
            </template>
            <template slot="view" :slot-scope="REC_devices.audio">
              <div class="-header">Select your microphone</div>
              <ul>
                <li
                  v-for="item in REC_devices.audio"
                  :key="item.id"
                  @click="changeAudioDevice(item.id)"
                  :class="{ '-selected': item.id === AUD_deviceId }"
                >
                  <i class="-check-icon wticon wticon-v_v8" />
                  <span :title="item.label">{{ item.label }}</span>
                </li>
              </ul>
              <div class="-volume" :class="{ '-disabled': AUD_isNoMicrophone }">
                <div class="-volume-slider-container">
                  Mic volume
                  <wt-slider
                    :min="0"
                    :max="2"
                    :disabled="
                      (!isPendingCapture && !REC_stream) || AUD_isNoMicrophone
                    "
                    :showStepsMarker="false"
                    :showInputBox="false"
                    :sliderStep="0.005"
                    v-model="AUD_gainValue"
                  >
                  </wt-slider>
                </div>
                <WtVolumeMeter :volume="AUD_volumeMeter" />
              </div>
            </template>
          </wt-dropdown-view>

          <wt-dropdown-view
            v-if="!hasRecording && !REC_isRecording && !showCountDown"
            v-show="!REC_isRecording"
            type="top"
            :disabled="
              REC_isRecording ||
              !REC_devices.video.length ||
              REC_videoDeviceLoading
            "
            ref="ddVideo"
          >
            <template slot="label">
              <span class="wticon wticon-camera2"></span>
              <span class="-label">{{ currentVideoDevice.label }}</span>
              <i class="-icon -chevron wticon wticon-chevron-down" />
            </template>
            <template slot="view" :slot-scope="REC_devices.video">
              <div class="-header">Select video source</div>
              <ul>
                <li
                  v-for="item in REC_devices.video"
                  :key="item.id"
                  @click="changeVideoDevice(item.id)"
                  :class="{ '-selected': item.id === REC_videoDeviceId }"
                >
                  <i class="-check-icon wticon wticon-v_v8" />
                  <span :title="item.label">{{ item.label }}</span>
                </li>
              </ul>
            </template>
          </wt-dropdown-view>
        </div>

        <div class="-center" v-if="REC_isRecording">
          {{ readableDuration }}
        </div>

        <div class="-buttons">
          <wt-button
            tabindex="1"
            type="transparent"
            :text="'Cancel'"
            v-if="!REC_isRecording"
            :disabled="showCountDown"
            @mouseup.native="closePrompt"
          ></wt-button>
          <wt-button
            tabindex="2"
            type="primary"
            v-if="!hasRecording"
            :disabled="!isPendingCapture && (showCountDown || !REC_stream)"
            :icon="REC_isRecording ? 'wticon-stop2' : 'wticon-camera2'"
            :class="{ '-recording': REC_isRecording }"
            :text="!REC_isRecording ? 'Start Recording' : 'Stop Recording'"
            @mouseup.native="startStopRecording"
          ></wt-button>
          <wt-button
            tabindex="2"
            type="hollow-dark"
            v-if="hasRecording"
            :text="'Start Over'"
            @mouseup.native="resetRecording"
          ></wt-button>
          <wt-button
            tabindex="3"
            type="primary"
            v-if="hasRecording"
            :text="'Save'"
            @mouseup.native="emitUpload"
          ></wt-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RecorderMixin from './mixins/RecorderMixin';
import AudioMixin from './mixins/AudioMixin';
import SpeechRecognitionMixin from './mixins/SpeechRecognitionMixin';
import WtVolumeMeter from './components/wtVolumeMeter.vue';
import wtFaceOnCameraCountdown from './components/wtFaceOnCameraCountdown.vue';
import Analytics from 'c/WtAnalytics';
import app from 'app';

// TODO: include ts-ebml in package.json
// TODO: import SafariRecorderMixin from './mixins/SafariRecorderMixin';
// const mixins = [RecorderMixin];
// if (!window.MediaRecorder) {
//   console.warn('Using Safari polyfill');
//   mixins.push(SafariRecorderMixin);
// }

export default {
  name: 'wtFaceOnCamera',

  mixins: [AudioMixin, RecorderMixin, SpeechRecognitionMixin],

  components: { WtVolumeMeter, wtFaceOnCameraCountdown },

  data() {
    return {
      videoBlob: null,
      debug: false,
      volumeAnimationFrame: null,
      showCountDown: false,
      countDownTimeout: null,
      audioMenuVisible: false,
      showContactSupport: !app.isEmbedded,
      autoTranscribe: false,
    };
  },

  props: {
    closeModal: { type: Function, required: false, default: () => {} },
    source: { type: String, required: false, default: 'independent' },
    useSpeechRecognition: { type: Boolean, required: false, default: false },
  },

  created() {
    this.startStream();
  },

  mounted() {
    Analytics.trigger('record:init', { from: this.source });
    this.$on('videoResult', (data) => {
      this.videoBlob = data;
      this.onVideoResult();
    });
    app.keyboardAccessibility.add('wt-face-on-camera', [
      {
        on: 'keydown',
        key: $.ui.keyCode.ESCAPE,
        run: this.closePrompt,
      },
      {
        on: 'keydown',
        key: $.ui.keyCode.SPACE,
        run: this.handleTapSpace,
      },
    ]);
  },

  beforeDestroy() {
    if (this.$refs.Video) {
      this.$refs.Video.srcObject = null;
    }
  },

  destroyed() {
    clearInterval(this.countDownTimeout);
    app.keyboardAccessibility.remove('wt-face-on-camera');
    this.volumeAnimationFrame = undefined;
  },

  methods: {
    handleTapSpace() {
      if (this.REC_isRecording) {
        this.startStopRecording();
      }
    },

    onShowHideAudioMenu(isShown) {
      this.audioMenuVisible = isShown;
    },

    changeVideoDevice(deviceId) {
      this.$refs.ddVideo.close();
      this.REC_changeDevice(deviceId);
      this.startStream();
      _.delay(() => {
        this.$refs.wtVideoPlayer.onCanPlay();
      }, 100);
    },

    changeAudioDevice(deviceId) {
      this.AUD_changeDevice(deviceId);
      this.AUD_startStream().then(async () => {
        await this.REC_replaceAudioTrack(this.AUD_destination.stream);
      });
    },

    resetRecording() {
      this.$refs.Video.pause();
      return app.dialog
        .confirm({
          type: app.dialog.types.CONFIRM_3,
          title: 'Are you sure?',
          description: 'Your recording will not be saved.',
          buttonYesText: 'Yes, record again',
          buttonNoText: app.culture.generic.CANCEL,
          buttonYesAction: true,
        })
        .then((data) => {
          if (data.action) {
            this.videoBlob = null;
            this.REC_screenPermissions = null;
            this.REC_resetTimings();
            this.startStream();
            Analytics.trigger('record:reset');
          }
        });
    },

    closePrompt() {
      if (this.REC_isRecording || this.videoBlob || this.showCountDown) {
        return app.dialog
          .confirm({
            type: app.dialog.types.CONFIRM_2,
            title: 'Your recording is not saved',
            buttonYesText: 'OK, delete',
            buttonNoText: app.culture.generic.CANCEL,
          })
          .then((data) => {
            if (data.action) {
              this.closeModal();
            }
          });
      }
      this.closeModal();
    },

    download(manual = true) {
      const fileFullName =
        'Wochit - Video Recorder - ' +
        app.moment().format('MMMM Do YYYY, h:mm:ss A') +
        '.webm';
      const hyperlink = document.createElement('a');
      hyperlink.href = URL.createObjectURL(this.videoBlob);
      hyperlink.download = fileFullName;

      hyperlink.style = 'display:none;opacity:0;color:transparent;';
      (document.body || document.documentElement).appendChild(hyperlink);

      if (typeof hyperlink.click === 'function') {
        hyperlink.click();
      } else {
        hyperlink.target = '_blank';
        hyperlink.dispatchEvent(
          new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
          })
        );
      }

      URL.revokeObjectURL(hyperlink.href);

      if (manual) {
        Analytics.trigger('record:download');
      }
    },

    startStream(options = { promptScreenRecording: false }) {
      return new Promise((resolve, reject) => {
        if (this.REC_isDisplayRecording && !options.promptScreenRecording) {
          return reject(
            'Screen recording pending, no need to startStream right now.'
          );
        }
        this.REC_verifySupported()
          .then(this.REC_getAvailableStreamDevices)
          .then(this.AUD_startStream)
          .then(this.REC_startStream)
          .then(() => {
            if (!this.AUD_destination) {
              return Promise.resolve(); // audio stream not mandatory for recording
            } else {
              return this.REC_replaceAudioTrack(this.AUD_destination.stream);
            }
          })
          .then(() => {
            this.showStream();
            resolve();
          })
          .catch((e) => {
            this.onStreamError(e);
            reject(e);
          });
      });
    },

    async startStopRecording() {
      const shouldCountdown = !this.REC_isDisplayRecording;
      if (this.REC_isRecording) {
        this._stopRecording();
      } else {
        if (this.REC_isDisplayRecording) {
          await this.startStream({ promptScreenRecording: true });
        }
        if (shouldCountdown) {
          this.showCountDown = true;
        }
        this.countDownTimeout = setTimeout(
          () => {
            this.REC_startRecording()
              .catch(this.onStreamError)
              .then(() => {
                if (this.$props.useSpeechRecognition) {
                  this.SPEECH_start();
                }
                this.showCountDown = false;
                this.countDownTimeout = null;
                this.REC_stream.getVideoTracks()[0].onended =
                  this._stopRecording;
              });
          },
          shouldCountdown ? 3000 : 0
        );
      }
    },

    _stopRecording() {
      this.REC_stopRecording();
      if (this.$props.useSpeechRecognition) {
        this.SPEECH_end();
      }
    },

    onVideoResult() {
      this.showVideoResult(this.videoBlob);
    },

    showStream() {
      if (!this.$refs.Video) {
        return;
      }
      this.$refs.Video.src = null;
      this.$refs.Video.srcObject = this.REC_stream;
      this.$refs.Video.play().catch(() => 0);
      this.$refs.Video.volume = 0;
      _.delay(() => {
        this.$refs.wtVideoPlayer.onCanPlay();
      }, 100);
    },

    async showVideoResult() {
      await this.$nextTick();
      if (!this.$refs.Video) {
        return;
      }
      this.$refs.Video.srcObject = null;
      this.$refs.Video.src = window.URL.createObjectURL(this.videoBlob);
      this.$refs.Video.volume = 1;
      _.delay(() => {
        this.$refs.wtVideoPlayer.onCanPlay();
      }, 100);
    },

    onStreamError(err) {
      if (this._isDestroyed) {
        return;
      }
      let title = 'Task Failed';
      let description = `Error: ${err.name ? err.name : err} <br>`;

      if (err.name == 'NotFoundError' || err.name == 'DevicesNotFoundError') {
        title = 'Required device is missing';
      } else if (
        err.name == 'NotReadableError' ||
        err.name == 'TrackStartError'
      ) {
        title = 'Camera or microphone are already in use';
      } else if (
        err.name == 'OverconstrainedError' ||
        err.name == 'ConstraintNotSatisfiedError'
      ) {
        title = 'Problem with your camera or microphone';
      } else if (
        err.name == 'NotAllowedError' ||
        err.name == 'PermissionDeniedError'
      ) {
        return;
      } else if (err.name == 'TypeError' || err.name == 'TypeError') {
        title = 'Internal Error';
        description = err.message;
      }

      app.dialog.error({
        type: app.dialog.types.ERROR_1,
        title,
        description,
        showContactCsm: true,
      });
    },

    $_onAutoTranscribeToggle(toggle) {
      const msDuration = 1e3 * this.REC_timings.duration;
      if (msDuration > app.TIME.minute(15)) {
        const estimatedTranscribeTime = 0.8 * msDuration;
        app.dialog
          .confirm({
            type: app.dialog.types.CONFIRM_3,
            title: 'Are you sure?',
            description: `It is going to take about ${app.moment
              .duration(estimatedTranscribeTime)
              .minutes()} min to transcribe this video. OK?`,
            buttonYesText: 'Ok, continue',
            buttonNoText: 'Maybe later',
            buttonNoAction: 'abort-transcribe',
          })
          .then((res) => {
            if (res.action === 'abort-transcribe') {
              this.$data.autoTranscribe = false;
            }
          });
      }
    },

    emitUpload() {
      const typeOfRecord = this.REC_isDisplayRecording
        ? 'Capture'
        : 'Face on camera';
      Analytics.trigger('record:upload', {
        duration: this.REC_timings.duration,
        Type: typeOfRecord,
      });
      if (this.$data.autoTranscribe) {
        Analytics.trigger('VO:autoTranscribeSelfUpload', {
          Type: typeOfRecord,
          'Asset duration': app.moment
            .utc(this.REC_timings.duration * 1e3)
            .format('m:ss'),
        });
      }

      this.download(false);

      this.closeModal({
        blob: this.videoBlob,
        caption: this.SPEECH_content,
        autoTranscribe: this.$data.autoTranscribe,
      });
    },

    contactSupport(errorType) {
      let message;
      switch (errorType) {
        case 1:
          message =
            "Hi. I'm having trouble with giving access to " +
            'record my screen the Video Recorder, can you assist?';
          break;
        default:
          message =
            "Hi. I'm having trouble with giving access the " +
            'microphone or camera in the Video Recorder, can you assist?';
      }

      app.intercomController.showNewMessage(message).catch(() => {
        // fallback - if there no intercom, go to website support
        app.openSupportTab();
      });
    },
  },

  computed: {
    isPendingCapture() {
      return (
        this.REC_isDisplayRecording &&
        !this.REC_isRecording &&
        !this.hasRecording
      );
    },

    recordSupported() {
      return (
        (!this.REC_isDisplayRecording && this.REC_cameraPermissions === true) ||
        (this.REC_isDisplayRecording && this.REC_screenPermissions === true)
      );
    },

    currentAudioDevice() {
      return (
        this.REC_devices.audio.find(
          (item) => item.id === this.AUD_deviceId
        ) || {
          label: this.REC_microphonePermissions
            ? 'Mic not detected'
            : 'No permissions...',
        }
      );
    },

    currentVideoDevice() {
      return (
        this.REC_devices.video.find(
          (item) => item.id === this.REC_videoDeviceId
        ) || {
          label: 'Camera not detected',
        }
      );
    },

    hasRecording() {
      return !!this.videoBlob;
    },

    readableDuration() {
      let duration = null;
      if (this.REC_timings.duration) {
        duration = app.moment
          .utc(this.REC_timings.duration * 1000)
          .format(this.REC_timings.duration > 59 * 60 ? 'mm:ss' : 'm:ss');
      }
      return duration;
    },
  },
};
</script>

<style lang="scss">
.wt-face-on-camera-container {
  .wt2-modal-content {
    width: 840px !important;
  }
}
</style>
<style lang="scss" scoped>
.wt-face-on-camera {
  &/deep/ {
    .wt2-player-video-container {
      display: flex; // fixed black margins for video
    }
  }
  position: relative;
  padding: 15px 20px 23px 20px;
  display: flex;
  flex-direction: column;

  .-disable-pointer {
    pointer-events: none;
  }

  .-debug-menu {
    position: absolute;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.75);
    z-index: 999;
    color: green;
    font-size: 15px;
    font-weight: 500;
    padding: 5px;
    pointer-events: none;

    > div {
      padding: 2px;
    }
  }

  .-video-container {
    position: relative;
    display: flex;
    margin-top: 10px;

    .-info-text {
      background-color: #f2f2f2;
      display: flex;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;

      .-title {
        font-size: 28px;
        font-weight: 300;
      }

      .-subtitle {
        font-size: 14px;
        margin-top: 10px;
        line-height: 20px;
        text-align: center;
      }
    }

    .-video-holder {
      position: relative;
      display: flex;
      width: 100%;
      min-height: 450px;
      background: rgb(0, 0, 0);
      video {
        min-height: 450px;
        max-height: 450px;
      }
      video {
        //pointer-events: none;
        width: 100%;
        background: black;
      }
    }

    .fade-countdown-enter-active,
    .fade-countdown-leave-active {
      transition: opacity 0.25s;
    }
    .fade-countdown-enter, .fade-countdown-leave-to /* .fade-leave-active in <2.1.8 */ {
      opacity: 0;
    }

    .-recording-icon {
      position: absolute;
      top: 15px;
      left: 15px;
      animation: breathe 2.5s infinite;
      height: 18px;
      width: 18px;
      background-color: #d0021b;
      border-radius: 50%;
    }

    @keyframes breathe {
      0% {
        opacity: 1;
      }
      30% {
        opacity: 1;
      }
      40% {
        opacity: 1;
      }
      50% {
        opacity: 1;
      }
      60% {
        opacity: 0.1;
      }
      70% {
        opacity: 1;
      }
      100% {
        opacity: 1;
      }
    }
  }

  .-bottom {
    margin-top: 23px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    position: relative;

    > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      .wt-button {
        height: 34px;
        border-radius: 3px;
        font-size: 14px;
        padding: 5px 15px;
        &.hollow-dark {
          border: 1px solid #d9d9d9;
        }
        &.-download {
          width: 34px;
          min-width: 34px;
          margin-right: 20px;
          .wticon {
            font-size: 18px;
          }
        }
        &.transparent {
          min-width: 0;
          width: 73px;
          padding: 0;
        }
        &/deep/ {
          .wticon-camera2 {
            font-size: 18px;
          }
          .wticon-stop2 {
            font-size: 14px;
          }
          .wticon-upload2,
          .wticon-download2 {
            font-size: 18px;
          }
        }
        &:not(:last-of-type) {
          margin-right: 10px;
        }
        &.-recording {
          background-color: #d0021b;
          &:hover {
            background-color: #bc000c;
          }
        }
      }
    }

    .-center {
      font-size: 16px;
      font-weight: 500;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    .-record-volume-meter {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: row;

      .wticon {
        font-size: 18px;
        margin-right: 10px;
      }
    }
  }

  .-recorder-icon {
    width: 64px;
    height: 64px;
    background-color: #4db6ac;
    border-radius: 50%;
    display: inline-block;
    text-align: center;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    cursor: pointer;
    &.-disabled {
      pointer-events: none;
      opacity: 0.5;
    }
  }
}

.wt2-dropdown-view-component {
  width: 202px;
  height: 34px;
  border-radius: 3px;
  border: 1px solid #d9d9d9;
  margin-right: 10px;
  &/deep/ {
    .wticon {
      font-size: 18px;
    }
    .-toggle {
      width: 100%;
      height: 100%;
      background: white;
      padding: 10px 15px;
      display: flex;
      flex-direction: row;
      &:hover,
      &.-opened {
        &:not([disabled]) {
          background-color: rgba(0, 0, 0, 0.05);
        }
      }
      .-label {
        flex-grow: 1;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-left: 10px;
      }
      .-chevron {
        font-size: 12px;
        margin: 0 0 0 15px;
      }
      &.-opened {
        .-chevron {
          transform: rotateX(180deg);
        }
      }
    }
    .-box {
      margin-top: -5px;
      width: 280px;
      margin-left: -1px;
      .-content {
        border-radius: 3px;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);
        .-header {
          color: rgba(0, 0, 0, 0.3);
          font-weight: 400;
          font-size: 14px;
          padding: 15px 0 10px 15px;
        }
        ul {
          padding: 0 0 5px 0;
          overflow: auto;
          max-height: 185px;
          li {
            position: relative;
            padding: 5px 20px 5px;
            padding-left: 40px;
            text-align: left;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;
            cursor: pointer;
            &:not(:first-of-type) {
              margin-top: 5px;
            }
            &:hover {
              background-color: rgba(0, 0, 0, 0.05);
              .-check-icon {
                opacity: 0;
              }
            }
            &.-selected {
              .-check-icon {
                opacity: 1;
              }
            }
            &.-disabled {
              pointer-events: none;
            }
            .-check-icon {
              position: absolute;
              transform: translateX(-100%);
              margin-left: -10px;
              margin-top: 5px;
              opacity: 0;
              font-size: 14px;
            }
          }
        }
        .-volume {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          padding: 5px 15px 20px 15px;
          border-top: 1px solid #d9d9d9;

          &.-disabled {
            pointer-events: none;
            opacity: 0.5;
          }

          .-volume-slider-container {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            width: 100%;
            font-size: 14px;
            font-weight: 400;

            .wt2-slider {
              flex-grow: 1;
              transform: translateY(3px);
              margin: 0 15px;
            }
          }
        }
      }
    }
  }
}
</style>
